import type {
  AppliedBand,
  AppliedFacetMode,
  AppliedQueryEntity
} from '@/types/searchItemsTypes'

/** Chip for a server-applied constraint, derived from the response envelope fields. */
export interface AutoAppliedChip {
  id: string
  kind: string
  label: string
  mode: string
  /** Query surface tokens behind the constraint; dismissal removes exactly these. */
  tokens: string[]
  /** Server veto: false means the constraint cannot be re-searched away. */
  removable: boolean
}

// Only these bands express "a query token matched a live facet value";
// residual/cosine_boost/semantic_fallback describe the text match itself, not a constraint.
const AUTO_CHIP_BANDS = new Set(['entity', 'occasion', 'aesthetic'])

function surfaceTokens(surface: AppliedQueryEntity['surface']): string[] {
  if (Array.isArray(surface)) return surface.filter(Boolean)
  return surface ? [surface] : []
}

/**
 * Entity chips are the canonical source (applied_entities, contract
 * 2026-07-25: every hard-bound pyramid entity, pyramid-rank order); the
 * facet/band derivation stays as the fallback for responses that predate it.
 */
export function deriveAutoAppliedChips(
  entities: AppliedQueryEntity[],
  facetModes: AppliedFacetMode[],
  bands: AppliedBand[],
  userSelectedOptions: Set<string>
): AutoAppliedChip[] {
  if (entities.length > 0) {
    return entities.map((entity) => ({
      id: `entity:${entity.kind}:${entity.value}`,
      kind: entity.kind,
      label: entity.label ?? entity.value,
      mode: 'hard',
      tokens: surfaceTokens(entity.surface),
      removable: entity.removable ?? true
    }))
  }

  const facetChips: AutoAppliedChip[] = facetModes
    .filter((facet) => !userSelectedOptions.has(facet.filter_option))
    .map((facet) => ({
      id: `facet:${facet.filter_option}`,
      kind: 'facet',
      label: facet.filter_option,
      mode: facet.mode,
      tokens: [],
      removable: true
    }))

  const bandChips: AutoAppliedChip[] = bands
    .filter(
      (band) =>
        AUTO_CHIP_BANDS.has(band.band) &&
        (band.mode === 'hard' || band.mode === 'boost') &&
        (band.consumed_tokens?.length ?? 0) > 0
    )
    .map((band) => ({
      id: `band:${band.band}`,
      kind: 'band',
      label: (band.consumed_tokens ?? []).join(' '),
      mode: band.mode,
      tokens: band.consumed_tokens ?? [],
      removable: true
    }))

  return [...facetChips, ...bandChips]
}

function escapeRegExp(source: string) {
  return source.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/**
 * Removes the given surface tokens (each may be a multi-word phrase) from the
 * query text on word boundaries. Returns null when some token is not present
 * verbatim — then there is no honest way to re-search without the constraint.
 * This is the ONLY sanctioned FE mutation of search_text; everything else
 * ships verbatim.
 */
export function removeSurfaceTokens(text: string, tokens: string[]): string | null {
  let result = text
  for (const token of tokens) {
    const pattern = new RegExp(`(^|\\s)${escapeRegExp(token)}(?=\\s|$)`, 'i')
    if (!pattern.test(result)) {
      return null
    }
    result = result.replace(pattern, '$1')
  }
  return result.replace(/\s+/g, ' ').trim()
}

export function isChipDismissible(chip: AutoAppliedChip, searchText: string): boolean {
  return (
    chip.removable &&
    chip.tokens.length > 0 &&
    removeSurfaceTokens(searchText, chip.tokens) !== null
  )
}
